import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Box,
  Typography,
  Modal,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useGrade } from "./hooks";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import Link from "@mui/material/Link";
import { GradingModelCard } from "../../components/gradingModel/Card";
import { NoGrades } from "../../components/gradingModel/NoGrades";
import Loader from "../../components/Loader";
import { Eye, X } from "@phosphor-icons/react";
import GradingModelView from "../course/lib/GradingModelView";
import { GradingModelPagination } from "../../components/gradingModel/Pagination";
import { category } from "./Edit";

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  padding: theme.spacing(1),
  height: "30px",
  border: "none",
  "&.Mui-selected": {
    background: "transparent",
    "& .MuiSvgIcon-root": {
      color: "var(--mui-palette-primary-orange)",
    },
  },
}));

export const GradingModel = () => {
  const { loadGrades, gradeList, loading, remove } = useGrade();
  const theme = useTheme();
  const [view, setView] = useState("card");
  const navigate = useNavigate();

  const [openPreview, setOpenPreview] = useState(false);
  const [previewData, setPreviewData] = useState();

  useEffect(() => {
    loadGrades();
  }, []);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const onDelete = (gradeId) => {
    if (!confirm("Are you sure to delete this grading model?")) {
      return;
    }
    remove({ id: gradeId });
  };

  const handlePreview = (grade) => {
    setOpenPreview(!openPreview);
    setPreviewData(grade);
  };

  const handleClose = () => {
    setOpenPreview(!openPreview);
    setPreviewData({});
  };

  const getMaxGradeMark = (details) => {
    if (details?.length > 0) {
      return Math.max(...details?.map((grade) => parseInt(grade.max, 10)));
    }
    return 0;
  };

  const columns = [
    {
      field: "gradingModelName",
      headerName: "Grading Model",
      flex: 1,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      renderCell: (params) => {
        return category[params.value] || params.value;
      },
    },
    {
      field: "details",
      headerName: "Number of Grades",
      flex: 1,
      renderCell: (params) => {
        return params.value.length;
      },
    },
    {
      field: "totalMarks",
      headerName: "Total Marks",
      flex: 1,
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: "passGrade",
      headerName: "Pass Grade",
      flex: 1,
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const grade = params.row;
        const editLink = `grading-model/edit/${grade?.id}`;
        return (
          <Box>
            <Link
              style={styles.actionBtnItem}
              href={editLink}
              title="Edit Grading"
            >
              <CreateIcon
                style={{
                  color: "var(--mui-palette-primary-button)",
                  fontSize: "1.2rem",
                }}
              />
            </Link>
            <Link
              style={styles.actionBtnItem}
              onClick={() => handlePreview(grade)}
              title="View Grading"
            >
              <Eye
                style={{
                  color: "var(--mui-palette-success-main)",
                  fontSize: "1.2rem",
                }}
              />
            </Link>
            <Link
              style={styles.actionBtnItem}
              onClick={() => onDelete(grade?.id)}
              title="Delete Grading"
            >
              <DeleteIcon
                style={{
                  color: "var(--mui-palette-error-main)",
                  fontSize: "1.2rem",
                }}
              />
            </Link>
          </Box>
        );
      },
    },
  ];

  const rows = gradeList?.data?.map((grade, index) => ({
    id: grade.id.trim(),
    gradingModelName: grade.gradingModelName,
    totalMarks: grade?.details[0]?.max,
    details: grade.details,
    category: grade?.category,
    passGrade: grade?.passGrade,
  }));

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="course">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography
                align="left"
                variant="h1"
                color="primary.blue"
                sx={{
                  margin: "0",
                  fontWeight: "700",
                  lineHeight: "1.5",
                  fontSize: "1.8rem",
                }}
              >
                Grading Model
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  [theme.breakpoints.down("sm")]: {
                    flexDirection: "row",
                  },
                }}
              >
                <ToggleButtonGroup
                  style={styles.toggleButtonContainer}
                  sx={{
                    [theme.breakpoints.down("sm")]: {
                      justifyContent: "flex-start",
                    },
                  }}
                  value={view}
                  exclusive
                  onChange={handleViewChange}
                  aria-label="course view"
                >
                  <StyledToggleButton
                    value="list"
                    aria-label="list view"
                    title="List View"
                    sx={{
                      padding: "5px",
                      [theme.breakpoints.down("sm")]: {
                        fontSize: "0.7rem",
                      },
                    }}
                  >
                    <TableRowsIcon />
                  </StyledToggleButton>
                  <StyledToggleButton
                    value="card"
                    aria-label="card view"
                    title="Card View"
                    sx={{
                      padding: "5px",
                      [theme.breakpoints.down("sm")]: {
                        fontSize: "0.7rem",
                      },
                    }}
                  >
                    <GridViewIcon />
                  </StyledToggleButton>
                </ToggleButtonGroup>
                <div style={styles.create}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate("/grading-model/create")}
                    title="Create Grades"
                    sx={{
                      background: "tranaparent",
                      color: "var(--mui-palette-primary-button)",
                      p: 0,
                    }}
                  >
                    <AddCircleIcon
                      sx={{ fontSize: "1.8rem", marginTop: "4px" }}
                    />
                  </Link>
                </div>
              </Box>
            </Box>
          </div>

          <Modal open={openPreview}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "80%",
                height: "80%",
                bgcolor: "background.paper",
                boxShadow: 24,
                borderRadius: "10px",
                p: 4,
                overflow: "auto",
              }}
            >
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              >
                <X />
              </IconButton>
              <GradingModelView
                totalMarks={getMaxGradeMark(previewData?.details)}
                data={previewData}
                gradeId={previewData?.id}
                grades={gradeList}
              />
              {/* <Preview question={previewData} /> */}
            </Box>
          </Modal>

          <Box
            sx={{
              clear: "both",
              width: "100%",
              float: "left",
              pt: 3,
              pb: 3,
              position: "relative",
              boxSizing: "border-box",
            }}
          >
            {view === "card" ? (
              <Grid
                container
                sx={{
                  position: "relative",
                  zIndex: 1,
                  marginLeft: "-8px",
                  marginRight: "-8px",
                  width: "auto",
                }}
              >
                {gradeList?.data?.length > 0 ? (
                  gradeList?.data?.map((grade, index) => (
                    <Grid
                      item
                      key={grade.id}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      sx={{ p: 1 }}
                    >
                      <GradingModelCard grade={grade} index={index} />
                    </Grid>
                  ))
                ) : (
                  <NoGrades />
                )}
              </Grid>
            ) : (
              <div style={{ minHeight: 500, width: "100%", overflow: "auto" }}>
                {gradeList?.data?.length > 0 ? (
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    sx={{
                      backgroundColor: "var(--mui-palette-common-white)",
                      [theme.breakpoints.down("sm")]: {
                        width: "900px",
                      },
                      "& .css-yrdy0g-MuiDataGrid-columnHeaderRow": {
                        background:
                          "var(--mui-palette-primary-blue) !important",
                        color: "var(--mui-palette-action-active)",
                      },
                      "& .MuiDataGrid-columnHeader": {
                        background:
                          "var(--mui-palette-primary-blue) !important",
                        color: "var(--mui-palette-common-white)",
                        "&:focus": {
                          outline: "none",
                        },
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontSize: "14px",
                        fontWeight: "bold",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "var(--mui-palette-common-white)",
                      },
                    }}
                    pageSize={5}
                  />
                ) : (
                  <NoGrades />
                )}
              </div>
            )}
            {gradeList?.data?.length > 0 && <GradingModelPagination />}
          </Box>
        </div>
      )}
    </>
  );
};
const styles = {
  toggleButtonContainer: {
    display: "flex",
    justifyContent: "center",
  },
  create: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "0",
    marginBottom: "0",
    marginLeft: "5px",
  },
  courses: {
    marginTop: "2rem",
  },
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "14px",
  },
  gridContainer: {
    mt: 2,
  },
  suggestionBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  suggestionItem: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    boxShadow: "0 4px 8px var(--mui-palette-secondary-boxShadow1)",
    backgroundColor: "var(--mui-palette-gray-lightGrey300)",
  },
  uploadBtn: {
    width: "50px",
    height: "50px",
    borderRadius: "5px",
  },
  hiddenInput: {
    display: "none",
  },
  actionBtnItem: {
    marginLeft: "5px",
    marginRight: "5px",
    cursor: "pointer",
  },
};
